/**
 * api/types — the JSON shapes the SPA consumes, mirroring the dashboard JSON API
 * (src/web/api.ts). Kept as a thin, independent copy so the frontend has no build
 * coupling to the server package; when a server contract changes, update it here in
 * lockstep. These match the *view-model* DTOs (`buildOverview`, `positionDto`,
 * `decisionDto`, `buildEquityCurvePayload`), NOT the persistence rows.
 */

export type FinalVerdict = 'ENTER' | 'WAIT' | 'REJECT';
export type PositionSide = 'LONG' | 'SHORT';
export type DataSource = 'live' | 'demo-seed';

export interface HaltState {
  readonly halted: boolean;
  readonly reason?: string | null;
}

/** GET /api/state — the lightweight auth/health probe (used at login). */
export interface StateResponse {
  readonly halt: HaltState;
  readonly demo: boolean;
  readonly env: 'testnet' | 'mainnet';
}

/**
 * The daily-loss governor (mirrors `DailyLossMeter`): realized P&L today measured
 * against the owner-fixed hard cap. `usedFraction` is the share of the loss budget
 * consumed (0 on a flat/green day, ≥1 when the cap is hit and trading auto-halts).
 */
export interface DailyLoss {
  readonly capPct: number;
  readonly usedFraction: number;
  readonly capHit: boolean;
  readonly realizedPct: number;
  readonly tradesToday: number;
}

/** GET /api/overview — the top-bar + hero + stat-tile numbers in one read. */
export interface OverviewResponse {
  readonly asOf: number;
  readonly source: DataSource;
  readonly demo: boolean;
  readonly equity: number;
  readonly startOfDayEquity: number;
  readonly dailyPnlPct: number;
  readonly dailyLoss: DailyLoss;
  readonly winrate: number;
  readonly avgR: number;
  readonly tradeCount: number;
  readonly wins: number;
  readonly losses: number;
  readonly drawdownPct: number;
  readonly halt: HaltState;
  /** Present when a sub-read degraded (e.g. trade history unavailable). */
  readonly partial?: boolean;
  readonly tradesError?: string;
}

/** One open position as shaped by `positionDto` (adds the derived `slArmed` flag). */
export interface LivePosition {
  readonly symbol: string;
  readonly side: PositionSide;
  readonly qty: number;
  readonly entryPrice: number;
  readonly markPrice: number;
  readonly notional: number;
  readonly leverage: number;
  readonly uPnl: number;
  readonly uPnlPct: number;
  /** True when a live protective stop is registered exchange-side. */
  readonly slArmed: boolean;
  readonly stopPrice: number | null;
  readonly openedAt: number;
  readonly seed: boolean;
}

/** GET /api/positions */
export interface PositionsResponse {
  readonly asOf: number;
  readonly source: DataSource;
  readonly equity: number;
  readonly startOfDayEquity: number;
  readonly positions: readonly LivePosition[];
}

export interface EquityPoint {
  readonly at: number;
  readonly equity: number;
}

export type EquityRange = '1d' | '7d' | '30d' | '90d' | 'all';

/** GET /api/equity-curve */
export interface EquityCurveResponse {
  readonly range: EquityRange;
  readonly startingEquity: number;
  readonly points: readonly EquityPoint[];
}

export interface RoleVote {
  readonly verdict: string;
  readonly confidence?: number;
}
export type CommitteeVotes = Record<string, RoleVote | undefined>;

export interface GateResult {
  readonly gate: string;
  readonly pass: boolean;
  readonly detail?: string;
}

/** A committee decision as shaped by `decisionDto` (verdict + 6 role votes + gates). */
export interface DecisionRecord {
  readonly id: number;
  readonly createdAt: string;
  readonly symbol: string;
  readonly timeframe: string;
  readonly setupType: string | null;
  readonly marketRegime: string | null;
  readonly verdict: FinalVerdict;
  readonly votes: CommitteeVotes;
  readonly gateResults: readonly GateResult[];
  readonly shadowOutcome: unknown;
}

/** GET /api/decisions (cursor-paginated, newest-first). */
export interface DecisionsResponse {
  readonly decisions: readonly DecisionRecord[];
  readonly nextCursor: number | null;
}

// ── open-position management / exit intelligence (EX.8) ──────────────────────────

/** The LLM position-manager committee verdict actually persisted by EX.5. */
export type PositionReviewAction = 'HOLD' | 'TIGHTEN_STOP' | 'EXIT_NOW';

/**
 * The flattened live-lifecycle picture (EX.1 `PositionLifecycle`) captured at review time
 * and extracted server-side from the review snapshot. Every field is nullable — a legacy
 * or partial row simply omits what it never recorded, and the view degrades gracefully.
 */
export interface PositionReviewLifecycle {
  readonly entryPrice: number | null;
  readonly markPrice: number | null;
  readonly stopPrice: number | null;
  readonly takeProfitPrice: number | null;
  readonly quantity: number | null;
  readonly initialQuantity: number | null;
  readonly unrealizedPnl: number | null;
  readonly unrealizedR: number | null;
  /** Peak favourable excursion so far, in R (how far the winner ran). */
  readonly peakMfeR: number | null;
  /** How far price has given back from the peak-MFE, price units. */
  readonly givebackFromPeak: number | null;
  /** Max adverse / favourable excursion, in R. */
  readonly maeR: number | null;
  readonly mfeR: number | null;
  readonly timeInTradeMs: number | null;
  /** Realised R / PnL already banked via scale-outs. */
  readonly realizedR: number | null;
  readonly realizedPnl: number | null;
  readonly scaleOutCount: number | null;
  readonly openedAt: number | null;
  readonly stopDistance: number | null;
}

/**
 * One persisted open-position committee review as shaped by `positionReviewDto`: the
 * HOLD / TIGHTEN_STOP / EXIT_NOW action + reasoning + the stop change it routed, plus the
 * live-lifecycle snapshot the management view draws its figures and stop-trail from.
 */
export interface PositionReviewRecord {
  readonly id: number;
  readonly createdAt: string;
  readonly decisionId: number | null;
  readonly symbol: string;
  readonly side: PositionSide;
  readonly action: PositionReviewAction;
  readonly confidence: number | null;
  /** The exit-bridge outcome status (SHADOW logs it, does not execute, until G0-EXIT). */
  readonly bridgeStatus: string | null;
  readonly holdReason: string | null;
  readonly intentId: string | null;
  readonly fromStop: number | null;
  readonly toStop: number | null;
  readonly unrealizedR: number | null;
  readonly reasoning: string | null;
  readonly lifecycle: PositionReviewLifecycle | null;
}

/** GET /api/position-reviews (newest-first, optionally scoped to one symbol). */
export interface PositionReviewsResponse {
  readonly reviews: readonly PositionReviewRecord[];
}

export type TradeResult = 'WIN' | 'LOSS' | 'BREAKEVEN';

/**
 * One closed trade as shaped by `tradeDto` (the trade_memories learning row plus
 * durable exchange execution facts when known).
 */
export interface TradeRecord {
  readonly id: number;
  readonly createdAt: string;
  readonly decisionId: number;
  readonly symbol: string;
  readonly timeframe: string;
  readonly setupType: string;
  readonly marketRegime: string;
  readonly newsRisk: string;
  readonly leverage: number;
  readonly riskPercent: number;
  readonly slDistancePercent: number | null;
  /** Planned reward:risk at entry (in R). */
  readonly rrPlanned: number | null;
  /** Realized R at close — the per-trade P&L in risk multiples. */
  readonly rrRealized: number | null;
  readonly result: TradeResult;
  readonly side: PositionSide | null;
  readonly quantity: number | null;
  readonly entryPrice: number | null;
  readonly exitPrice: number | null;
  readonly grossPnlUsd: number | null;
  readonly commissionUsd: number | null;
  readonly fundingUsd: number | null;
  readonly netPnlUsd: number | null;
  readonly netR: number | null;
  /** Max adverse excursion, in R (how far offside the trade went before closing). */
  readonly mae: number | null;
  /** Max favorable excursion, in R (the best unrealized R the trade reached). */
  readonly mfe: number | null;
  readonly entryReason: string | null;
  readonly notes: string | null;
}

/** GET /api/trades (cursor-paginated, newest-first). */
export interface TradesResponse {
  readonly trades: readonly TradeRecord[];
  readonly nextCursor: number | null;
}

/**
 * One `setup_stats` bucket — a rolling aggregate over closed trades keyed by
 * (setupType, marketRegime, fundingBucket, leverageBucket). Mirrors `setupStatDto`
 * (the `SetupStatsRecord` with its `updatedAt` serialized to ISO). Winrate is a
 * fraction in [0,1]; the R figures are risk multiples; the MAE/MFE p50/p90 are the
 * excursion percentiles in R (null until the bucket has samples).
 */
export interface SetupStat {
  readonly setupType: string;
  readonly marketRegime: string;
  readonly fundingBucket: string;
  readonly leverageBucket: string;
  readonly n: number;
  readonly wins: number;
  readonly winrate: number;
  readonly avgRrPlanned: number | null;
  readonly avgRrRealized: number | null;
  readonly maeP50: number | null;
  readonly maeP90: number | null;
  readonly mfeP50: number | null;
  readonly mfeP90: number | null;
  readonly updatedAt: string;
}

/**
 * One `agent_scorecards` row — how often a committee role's call matched the eventual
 * outcome. Mirrors `scorecardDto`. precision/recall are fractions in [0,1] (null when
 * the denominator is empty, e.g. a role that has never voted ENTER yet).
 */
export interface AgentScorecard {
  readonly role: string;
  readonly n: number;
  readonly truePositive: number;
  readonly falsePositive: number;
  readonly trueNegative: number;
  readonly falseNegative: number;
  readonly precision: number | null;
  readonly recall: number | null;
  readonly updatedAt: string;
}

/**
 * GET /api/stats — the learning aggregates. `seed` is a demo-only flag the mock API
 * sets so the SPA can label fixture data honestly; the real backend omits it.
 */
export interface StatsResponse {
  readonly setupStats: readonly SetupStat[];
  readonly scorecards: readonly AgentScorecard[];
  readonly seed?: boolean;
}

// ── decision-funnel analytics (TA.1b) ────────────────────────────────────────────

/**
 * The committee decision funnel as computed server-side by `buildDecisionsAnalytics`
 * (GET /api/analytics/decisions). Every decision enters the committee, so
 * `candidates === committeeRun`; the narrowing is candidates → ENTER, with the
 * non-ENTER split into WAIT + REJECT (`noTrade = wait + reject`).
 */
export interface DecisionFunnel {
  readonly candidates: number;
  readonly committeeRun: number;
  readonly enter: number;
  readonly wait: number;
  readonly reject: number;
  readonly noTrade: number;
}

/** One NO-TRADE reason: a gate that failed on a non-ENTER decision, tallied by gate name. */
export interface ReasonCount {
  readonly gate: string;
  readonly count: number;
  /** The first failure detail seen for this gate (drill-down copy); null when the gate carries none. */
  readonly sampleReason: string | null;
}

/** One 10%-wide confidence bucket (`0-9` … `90-99`) with how many votes fell in it. */
export interface ConfidenceBucket {
  readonly label: string;
  readonly count: number;
}

/** How often a role's directional call matched the Arbiter's, over decisions where both are readable. */
export interface AgreementRate {
  readonly role: string;
  readonly n: number;
  readonly agree: number;
  readonly rate: number;
}

/** GET /api/analytics/decisions — the full server-computed decision-funnel surface. */
export interface DecisionsAnalyticsResponse {
  readonly funnel: DecisionFunnel;
  readonly noTradeReasons: readonly ReasonCount[];
  readonly signalConfidence: readonly ConfidenceBucket[];
  readonly arbiterConfidence: readonly ConfidenceBucket[];
  readonly roleScorecards: readonly AgentScorecard[];
  readonly agreementRates: readonly AgreementRate[];
  /** Set when the scorecard read failed but the funnel is still served (degraded). */
  readonly partial?: boolean;
  readonly scorecardsError?: string;
}

/** GET /api/analytics/summary — headline KPIs (we read `totalTrades` for the funnel's executed tail). */
export interface AnalyticsSummaryResponse {
  readonly totalTrades: number;
  readonly wins: number;
  readonly losses: number;
  readonly breakeven: number;
  readonly winrate: number;
  readonly netWinrate: number;
  readonly avgR: number | null;
  readonly totalPnlUsd: number;
  readonly profitFactor: number | null;
  readonly maxDrawdownPct: number;
  readonly sharpeIsh: number | null;
}

// ── settings (TF.6) ────────────────────────────────────────────────────────────

/** The committee's advisory roles, in canonical order (mirrors COMMITTEE_ROLES). */
export const COMMITTEE_ROLES = ['signal', 'bear', 'risk', 'news', 'historian', 'arbiter'] as const;
export type CommitteeRole = (typeof COMMITTEE_ROLES)[number];

/** Human labels for each role (mirrors the server-rendered ROLE_LABEL). */
export const ROLE_LABEL: Readonly<Record<CommitteeRole, string>> = {
  signal: 'Signal',
  bear: 'Bear-case',
  risk: 'Risk officer',
  news: 'News / Macro',
  historian: 'Historian',
  arbiter: 'Arbiter',
};

/** Model tiers the committee accepts (mirrors ALLOWED_MODELS server-side). */
export const ALLOWED_MODELS = ['haiku', 'sonnet', 'opus'] as const;
export type ModelTier = (typeof ALLOWED_MODELS)[number];

/** Candle intervals a symbol's cadence may be set to (mirrors ALLOWED_INTERVALS). */
export const ALLOWED_INTERVALS = [
  '1m', '3m', '5m', '15m', '30m', '1h', '2h', '4h', '6h', '8h', '12h', '1d',
] as const;
export type CandleInterval = (typeof ALLOWED_INTERVALS)[number];

/** Blackout event types tracked alongside the committed econ calendar. */
export const BLACKOUT_TYPES = ['FOMC', 'CPI', 'NFP'] as const;
export type BlackoutType = (typeof BLACKOUT_TYPES)[number];

/** Per-role committee setting: on/off plus its model tier. */
export interface RoleSetting {
  readonly enabled: boolean;
  readonly model: string;
}

/** One additive blackout window layered on top of the committed static calendar. */
export interface CustomBlackout {
  readonly type: string;
  readonly label: string;
  /** Release instant, ISO-8601 (e.g. '2026-08-13T12:30:00Z'). */
  readonly time: string;
}

export interface NewsSettings {
  readonly cryptoPanicEnabled: boolean;
  readonly rssFeeds: readonly string[];
}

export interface BudgetSettings {
  readonly dailyTokenBudget: number;
  readonly perRoleTokenCap: Readonly<Record<string, number>>;
}

/** The full tunable settings object the SPA reads and writes over /api/settings. */
export interface TunableSettings {
  readonly symbols: readonly string[];
  readonly cadence: Readonly<Record<string, string>>;
  readonly roles: Readonly<Record<string, RoleSetting>>;
  readonly budget: BudgetSettings;
  readonly news: NewsSettings;
  readonly blackout: { readonly customEvents: readonly CustomBlackout[] };
}

/**
 * GET /api/settings — the tunable settings plus the owner-only risk limits as a
 * read-only display map (label → formatted value). The SPA has NO edit path for the
 * latter; a PUT carrying a risk-shaped field is rejected 400 server-side.
 */
export interface SettingsResponse {
  readonly settings: TunableSettings;
  readonly riskLimits: Readonly<Record<string, string>>;
}

/** PUT /api/settings — echoes exactly what landed on disk (the round-trip contract). */
export interface SettingsSaveResponse {
  readonly ok: true;
  readonly settings: TunableSettings;
}

/** POST /api/killswitch */
export interface KillOutcome {
  readonly halted: boolean;
  readonly reason: string;
  readonly flattened: readonly string[];
  readonly cancelled: readonly string[];
  readonly errors: readonly string[];
  readonly dryRun: boolean;
}

/** POST /api/rearm — clears the durable halt; echoes the post-clear halt state. */
export interface ReArmResponse {
  readonly ok: true;
  readonly halt: HaltState;
}
